import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import type {
  SortingState,
  Header,
  PaginationState,
  RowSelectionState,
  ColumnDef,
} from '@tanstack/react-table';
import { ArrowUp, ArrowDown } from 'lucide-react';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { DataTableSkeleton } from './DataTableSkeleton';
import { DataTableEmpty } from './DataTableEmpty';
import { ColumnVisibilityToggle } from './ColumnVisibilityToggle';
import { DataTableSearch } from './DataTableSearch';
import { DataTablePagination } from './DataTablePagination';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import type { DataTableProps } from './types';
import type { Table as TableType } from '@tanstack/react-table';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_STICKY_COL_WIDTH = 150;

// ---------------------------------------------------------------------------
// Sort Header Renderer
// ---------------------------------------------------------------------------

function SortableHeader<TData>({ header }: { header: Header<TData, unknown> }) {
  const canSort = header.column.getCanSort();
  const sortDirection = header.column.getIsSorted();
  const columnId = header.column.id;

  if (!canSort) {
    return <>{flexRender(header.column.columnDef.header, header.getContext())}</>;
  }

  return (
    <button
      type="button"
      className="flex items-center gap-1 select-none"
      data-testid={`sort-header-${columnId}`}
      onClick={header.column.getToggleSortingHandler()}
    >
      {flexRender(header.column.columnDef.header, header.getContext())}
      {sortDirection === 'asc' && <ArrowUp className="h-4 w-4" data-testid="sort-asc" />}
      {sortDirection === 'desc' && <ArrowDown className="h-4 w-4" data-testid="sort-desc" />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Selection column factory (pure)
// ---------------------------------------------------------------------------

function buildSelectionColumn<TData>(): ColumnDef<TData, unknown> {
  return {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={value => row.toggleSelected(!!value)}
        aria-label={`Select row ${row.index + 1}`}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  };
}

// ---------------------------------------------------------------------------
// Sticky column offset calculator
// ---------------------------------------------------------------------------

function getStickyOffset(
  colIndex: number,
  visibleColumns: ReadonlyArray<{ getSize: () => number }>
): number {
  let offset = 0;
  for (let i = 0; i < colIndex; i++) {
    offset += visibleColumns[i]?.getSize() ?? DEFAULT_STICKY_COL_WIDTH;
  }
  return offset;
}

// ---------------------------------------------------------------------------
// DataTable Component
// ---------------------------------------------------------------------------

export function DataTable<TData>({
  data,
  columns,
  isLoading = false,
  emptyMessage = 'No results found',
  emptyActionLabel,
  onEmptyAction,
  className,
  enableColumnVisibility = false,
  tableId,
  enableSorting = false,
  manualSorting = false,
  initialSorting,
  onSortChange,
  enableSearch = false,
  searchPlaceholder,
  manualFiltering = false,
  onSearchChange,
  // Pagination
  enablePagination = false,
  pageSize: initialPageSize,
  pageIndex: controlledPageIndex,
  pageCount: controlledPageCount,
  totalRows: externalTotalRows,
  manualPagination = false,
  onPaginationChange,
  // Row interactions
  onRowClick,
  enableRowSelection = false,
  onRowSelectionChange,
  onTableReady,
  onColumnVisibilityChange,
  // Contained scrolling & layout
  maxHeight,
  stickyColumns = 0,
  footer,
  // Table meta for custom cell interactions
  meta,
  // Forced hidden/visible columns (override, not persisted)
  forcedHiddenColumns,
  forcedVisibleColumns,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: controlledPageIndex ?? 0,
    pageSize: initialPageSize ?? DEFAULT_PAGE_SIZE,
  });
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Sync controlled pageIndex from parent
  useEffect(() => {
    if (controlledPageIndex !== undefined) {
      setPagination(prev => ({
        ...prev,
        pageIndex: controlledPageIndex,
      }));
    }
  }, [controlledPageIndex]);

  const { columnVisibility, setColumnVisibility, persistVisibility } = useColumnVisibility({
    columns,
    tableId,
  });

  // Merge user column visibility with forced hidden/visible columns (not persisted)
  const effectiveColumnVisibility = useMemo(() => {
    const hasHidden = forcedHiddenColumns && forcedHiddenColumns.length > 0;
    const hasVisible = forcedVisibleColumns && forcedVisibleColumns.length > 0;
    if (!hasHidden && !hasVisible) return columnVisibility;
    const overrides: Record<string, boolean> = {};
    if (hasHidden) {
      for (const colId of forcedHiddenColumns) {
        overrides[colId] = false;
      }
    }
    if (hasVisible) {
      for (const colId of forcedVisibleColumns) {
        overrides[colId] = true;
      }
    }
    return { ...columnVisibility, ...overrides };
  }, [columnVisibility, forcedHiddenColumns, forcedVisibleColumns]);

  // Build merged columns with selection column if needed
  const mergedColumns = useMemo(() => {
    if (!enableRowSelection) {
      return columns;
    }
    return [buildSelectionColumn<TData>(), ...columns];
  }, [columns, enableRowSelection]);

  const handleSortingChange = useCallback(
    (updater: SortingState | ((prev: SortingState) => SortingState)) => {
      setSorting(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (onSortChange) {
          onSortChange(next);
        }
        return next;
      });
    },
    [onSortChange]
  );

  const handleGlobalFilterChange = useCallback(
    (value: string) => {
      setGlobalFilter(value);
      if (onSearchChange) {
        onSearchChange(value);
      }
    },
    [onSearchChange]
  );

  const handlePaginationChange = useCallback(
    (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => {
      setPagination(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (onPaginationChange) {
          onPaginationChange(next);
        }
        return next;
      });
    },
    [onPaginationChange]
  );

  const handleRowSelectionChange = useCallback(
    (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      setRowSelection(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        return next;
      });
    },
    []
  );

  // Build table state
  const tableState: Record<string, unknown> = {};
  if (enableColumnVisibility) {
    tableState.columnVisibility = effectiveColumnVisibility;
  }
  if (enableSorting) {
    tableState.sorting = sorting;
  }
  if (enableSearch) {
    tableState.globalFilter = globalFilter;
  }
  if (enablePagination) {
    tableState.pagination = pagination;
  }
  if (enableRowSelection) {
    tableState.rowSelection = rowSelection;
  }

  const table = useReactTable({
    data,
    columns: mergedColumns,
    getCoreRowModel: getCoreRowModel(),
    // Sorting
    ...(enableSorting && !manualSorting ? { getSortedRowModel: getSortedRowModel() } : {}),
    ...(enableSorting
      ? {
          onSortingChange: handleSortingChange,
          manualSorting,
          enableSorting: true,
        }
      : {}),
    // Filtering
    ...(enableSearch && !manualFiltering
      ? {
          getFilteredRowModel: getFilteredRowModel(),
          globalFilterFn: 'includesString',
        }
      : {}),
    ...(enableSearch
      ? {
          onGlobalFilterChange: setGlobalFilter,
          manualFiltering,
        }
      : {}),
    // Pagination
    ...(enablePagination && !manualPagination
      ? { getPaginationRowModel: getPaginationRowModel() }
      : {}),
    ...(enablePagination
      ? {
          onPaginationChange: handlePaginationChange,
          manualPagination,
          ...(controlledPageCount !== undefined ? { pageCount: controlledPageCount } : {}),
        }
      : {}),
    // Row selection
    ...(enableRowSelection
      ? {
          enableRowSelection: true,
          onRowSelectionChange: handleRowSelectionChange,
        }
      : {}),
    // State
    state: Object.keys(tableState).length > 0 ? tableState : undefined,
    // Column visibility
    onColumnVisibilityChange: enableColumnVisibility
      ? updater => {
          setColumnVisibility(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            persistVisibility(next);
            return next;
          });
        }
      : undefined,
    // Custom meta for cell interactions
    meta,
  });

  // Expose table instance to parent consumers (e.g., Chart View)
  useEffect(() => {
    if (onTableReady) {
      onTableReady(table);
    }
  }, [table, onTableReady]);

  // Notify parent of column visibility changes (for chart synchronization)
  useEffect(() => {
    if (onColumnVisibilityChange) {
      const visibleKeys = table.getVisibleLeafColumns().map(col => col.id);
      onColumnVisibilityChange(visibleKeys);
    }
  }, [table.getState().columnVisibility, onColumnVisibilityChange, table]);

  // Notify parent of row selection changes
  useEffect(() => {
    if (!enableRowSelection || !onRowSelectionChange) {
      return;
    }
    const selectedRows = table.getSelectedRowModel().rows.map(row => row.original);
    onRowSelectionChange(selectedRows);
  }, [rowSelection, enableRowSelection, onRowSelectionChange, table]);

  if (isLoading) {
    return <DataTableSkeleton columnCount={columns.length} />;
  }

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const isEmpty = rows.length === 0;
  const hasNoVisibleColumns = table.getVisibleLeafColumns().length === 0;
  const isClickable = Boolean(onRowClick);
  const visibleColumns = table.getVisibleLeafColumns();

  const showToolbar = enableColumnVisibility || enableSearch;

  // Calculate totalRows for pagination info
  const totalRows = externalTotalRows ?? data.length;
  const showPagination = enablePagination && totalRows > 0;

  // Whether we use contained scrolling mode
  const isContained = Boolean(maxHeight);

  return (
    <div
      className={cn(
        'rounded-lg border border-v11-border bg-v11-surface',
        isContained && 'flex flex-col',
        className
      )}
      style={isContained ? { maxHeight } : undefined}
      data-testid="data-table"
    >
      {showToolbar && (
        <div
          className={cn(
            'flex items-center gap-2 px-4 py-2 bg-v11-bg-secondary border-b border-v11-border',
            isContained && 'flex-shrink-0'
          )}
        >
          {enableSearch && (
            <div className="flex-1">
              <DataTableSearch
                value={globalFilter}
                onChange={handleGlobalFilterChange}
                placeholder={searchPlaceholder}
              />
            </div>
          )}
          {enableColumnVisibility && (
            <div className={enableSearch ? '' : 'ml-auto'}>
              <ColumnVisibilityToggle
                table={table as unknown as TableType<unknown>}
                tableId={tableId}
                excludeColumns={forcedHiddenColumns}
              />
            </div>
          )}
        </div>
      )}

      {/* Scroll container (or passthrough) */}
      <div className={cn(isContained && 'flex-1 overflow-auto min-h-0 v11-scrollbar')}>
        <Table wrapperClassName={isContained ? 'relative w-full overflow-visible' : undefined}>
          <TableHeader>
            {headerGroups.map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, colIdx) => {
                  const isSticky = colIdx < stickyColumns;
                  const stickyStyle = isSticky
                    ? {
                        position: 'sticky' as const,
                        left: getStickyOffset(colIdx, visibleColumns),
                        zIndex: 15,
                      }
                    : undefined;
                  const align = (header.column.columnDef.meta as { align?: string })?.align;
                  const alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
                  const colSize = header.column.columnDef.size;
                  const colWidth = colSize ? `${colSize}px` : undefined;

                  return (
                    <TableHead
                      key={header.id}
                      style={{ ...stickyStyle, width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                      className={cn(isSticky && 'bg-v11-bg-secondary', alignClass)}
                    >
                      {header.isPlaceholder ? null : enableSorting ? (
                        <SortableHeader header={header} />
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {hasNoVisibleColumns ? (
              <DataTableEmpty
                variant="no-columns"
                message="Select at least one column from the Columns button to display data in the table."
                columnCount={columns.length}
              />
            ) : isEmpty ? (
              <DataTableEmpty
                variant="no-results"
                message={emptyMessage}
                columnCount={table.getVisibleLeafColumns().length || columns.length}
                actionLabel={emptyActionLabel}
                onAction={onEmptyAction}
              />
            ) : (
              rows.map(row => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  className={cn(isClickable && 'cursor-pointer')}
                  tabIndex={isClickable ? 0 : undefined}
                  onClick={isClickable ? () => onRowClick!(row.original) : undefined}
                  onKeyDown={
                    isClickable
                      ? e => {
                          if (e.key === 'Enter') {
                            onRowClick!(row.original);
                          }
                        }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell, colIdx) => {
                    const isSticky = colIdx < stickyColumns;
                    const stickyStyle = isSticky
                      ? {
                          position: 'sticky' as const,
                          left: getStickyOffset(colIdx, visibleColumns),
                          zIndex: 5,
                        }
                      : undefined;
                    const align = (cell.column.columnDef.meta as { align?: string })?.align;
                    const alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
                    const colSize = cell.column.columnDef.size;
                    const colWidth = colSize ? `${colSize}px` : undefined;

                    return (
                      <TableCell
                        key={cell.id}
                        style={{ ...stickyStyle, width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                        className={cn(isSticky && 'bg-v11-bg-secondary', colWidth && 'overflow-hidden', alignClass)}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Footer (outside scroll) */}
      {footer && <div className="flex-shrink-0">{footer}</div>}

      {showPagination && (
        <DataTablePagination
          table={table as unknown as TableType<unknown>}
          totalRows={totalRows}
          manualPagination={manualPagination}
        />
      )}
    </div>
  );
}
